import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, X } from 'lucide-react';

// Combobox de modelo: busca por nome, mostra só os modelos do PRÓPRIO
// usuário (a lista vem sempre de `templates`, nunca da comunidade), e
// termina com um link explícito para a comunidade — quem quer navegar
// pelos modelos públicos sabe exatamente que está saindo dos seus.
// `onChange` recebe um evento sintético { target: { value } } para caber
// sem mudanças nos handlers existentes (mesma assinatura de um <select>).
const TemplatePicker = ({ id, templates, value, onChange, placeholder = 'Nenhum selecionado' }) => {
  const selected = templates.find((t) => t.id === value) || null;
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const filtered = query.trim()
    ? templates.filter((t) => t.name.toLowerCase().includes(query.trim().toLowerCase()))
    : templates;

  const select = (templateId) => {
    onChange({ target: { value: templateId } });
    setIsOpen(false);
    setQuery('');
  };

  return (
    <div ref={wrapperRef} className="relative w-64">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted w-3.5 h-3.5 pointer-events-none" aria-hidden="true" />
        <input
          id={id}
          type="text"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${id}-list`}
          className="input-field w-full text-sm py-2 pl-9 pr-8"
          placeholder={placeholder}
          value={isOpen ? query : (selected?.name || '')}
          onFocus={() => { setIsOpen(true); setQuery(''); }}
          onChange={(e) => setQuery(e.target.value)}
        />
        {selected && !isOpen && (
          <button
            type="button"
            onClick={() => select('')}
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
            aria-label="Limpar modelo selecionado"
          >
            <X className="w-3.5 h-3.5" aria-hidden="true" />
          </button>
        )}
      </div>

      {isOpen && (
        <div
          id={`${id}-list`}
          role="listbox"
          className="absolute z-20 mt-1.5 w-full bg-paper border border-line rounded-card shadow-card max-h-64 overflow-y-auto"
        >
          <button
            type="button"
            role="option"
            onClick={() => select('')}
            className="w-full text-left px-3 py-2 text-xs text-muted hover:bg-paper-alt border-b border-line"
          >
            {placeholder}
          </button>

          {filtered.length > 0 ? (
            filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                role="option"
                aria-selected={t.id === value}
                onClick={() => select(t.id)}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-paper-alt transition-colors ${t.id === value ? 'text-navy font-semibold bg-paper-alt' : 'text-ink'}`}
              >
                {t.name}
              </button>
            ))
          ) : (
            <p className="px-3 py-3 text-xs text-muted">
              {templates.length === 0 ? 'Você ainda não criou nenhum modelo.' : 'Nenhum modelo com esse nome.'}
            </p>
          )}

          <Link
            to="/modelos?tab=community"
            className="block px-3 py-2.5 text-xs font-semibold text-navy hover:bg-paper-alt border-t border-line"
          >
            Pesquisar modelos da comunidade
          </Link>
        </div>
      )}
    </div>
  );
};

export default TemplatePicker;
